import { Box, Grid, IconButton, ImageListItem, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ServiceItem } from 'orderme-api-integration-client';
import { Linear } from '../progressBars/linear';
import { HtmlViewer } from '../../controls/html/htmlViewer';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { ImageLoader } from '../imgLoader/imgLoader';
import { apiKey, appStyleMode, imagesUri } from '../../helpers/clientConfigs';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AppStyle } from '../../helpers/appStyle';
import { BottomCartBar } from '../appBars/cartBar/bottomCartBar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DiscountImage, DiscountTypography } from '../serviceLogoItem/serviceLogoItemStyles';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import { Navigation, Thumbs } from 'swiper/modules';
import { AddToCartActionBox } from './addToCartActionBox';
import { BuyCreditsActionBox } from './buyCreditsActionBox';

export type ActionBoxType = 'addToCart' | 'buyCredits';

export interface ServiceItemViewProps {
  service: ServiceItem | undefined;
  actionType: ActionBoxType;
}

function ActionBox(props: { actionType: ActionBoxType; service: ServiceItem; visible: boolean; isSticky?: boolean }) {
  const { actionType, ...actionProps } = props;
  switch (actionType) {
    case 'addToCart':
      return <AddToCartActionBox {...actionProps} />;
    case 'buyCredits':
      return <BuyCreditsActionBox {...actionProps} />;
  }
}

export function ServiceItemView(props: ServiceItemViewProps) {
  const { service, actionType } = props;
  const routeNavigator = useRouteNavigator();
  const theme = useTheme();
  const { t } = useTranslation();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [serviceImages, setServiceImages] = useState<string[]>();
  const [description, setDescription] = useState<string>();
  const swiperRef = useRef<SwiperType | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [isDescriptionExpandable, setIsDescriptionExpandable] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const isDownMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallSize = useMediaQuery('(max-width:499px)');
  const isExtraSmallSize = useMediaQuery('(max-width:350px)');
  const maxVisibleImagesCount = isExtraSmallSize ? 2 : isSmallSize ? 3 : 4;
  const visibleImagesCount = Math.min(maxVisibleImagesCount, serviceImages?.length ?? maxVisibleImagesCount);

  const collapseDescription = useCallback(() => {
    const endOfParagraph = service.description.indexOf('</p>');
    const shortDescription =
      endOfParagraph > 0 ? service.description.substring(0, endOfParagraph + 4) : service.description;
    setDescription(shortDescription);
    setIsDescriptionExpandable(shortDescription !== service.description);
    setIsExpanded(false);
  }, [service]);

  useEffect(() => {
    if (service) {
      if (service.images && service.images.length > 0) {
        setServiceImages([service.logoId, ...service.images.map((image) => image.imageId)]);
      } else {
        setServiceImages([service.logoId]);
      }

      if (!service.logoId) {
        setIsImageLoaded(true);
      }

      if (service.description) {
        collapseDescription();
      } else {
        setDescription(null);
        setIsDescriptionExpandable(false);
        setIsExpanded(false);
      }
    }
  }, [collapseDescription, service]);

  useEffect(() => {
    if (serviceImages && swiperRef.current) {
      swiperRef.current.slideTo(0);
    }
  }, [serviceImages]);

  const expandDescription = () => {
    setDescription(service.description);
    setIsExpanded(true);
  };

  return (
    <>
      {(!service || !isImageLoaded) && <Linear />}
      {service && (
        <Grid
          sx={{
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '15px',
            color: 'black',
            pt: appStyleMode === AppStyle.Mobile ? '0px' : '20px',
            pb: '15px',
            display: !isImageLoaded ? 'none' : null,
          }}
        >
          {appStyleMode === AppStyle.Mobile && (
            <IconButton
              sx={{
                position: 'absolute',
                zIndex: 2,
                color: 'black',
                backgroundColor: 'white',
                opacity: 0.75,
                ml: '5px',
                mt: '5px',
              }}
              onClick={() => routeNavigator.goBack()}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box className={appStyleMode === AppStyle.Mobile && 'goods-item'}>
            <Grid
              container
              sx={{
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'center', md: 'start' },
              }}
            >
              {serviceImages && (
                <Grid
                  container
                  sx={{
                    flexDirection: 'column',
                    textAlign: 'center',
                    width: isSmallSize ? '100%' : '440px',
                  }}
                >
                  <Box
                    sx={{
                      px: appStyleMode === AppStyle.Mobile ? '0px' : '20px',
                      width: isSmallSize ? '100%' : '440px',
                      boxSizing: 'border-box',
                      display: !isImageLoaded ? 'none' : undefined,
                      '& .swiper-button-next, & .swiper-button-prev': {
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        borderRadius: '50%',
                        width: '30px',
                        height: '30px',
                        padding: '10px',
                        color: 'primary.main',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                      },
                      '&:hover .swiper-button-next, &:hover .swiper-button-prev': {
                        opacity: 1,
                      },
                    }}
                  >
                    <Swiper
                      modules={[Navigation, Thumbs]}
                      slidesPerView={1}
                      navigation
                      thumbs={{ swiper: thumbsSwiper }}
                      onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                      }}
                      loop={serviceImages.length > 1}
                      style={{
                        height: isSmallSize ? '300px' : '400px',
                        borderRadius: appStyleMode === AppStyle.Mobile ? '0px' : '10px',
                        display: !isImageLoaded ? 'none' : null,
                      }}
                    >
                      {serviceImages.map((imageId, index) => (
                        <SwiperSlide key={imageId ?? index}>
                          {service?.discount > 0 && (
                            <>
                              <DiscountImage />
                              <DiscountTypography> -{service.discount} %</DiscountTypography>
                            </>
                          )}
                          <ImageListItem
                            sx={{
                              width: '100%',
                              height: '100% !important',
                            }}
                          >
                            <ImageLoader
                              sx={{
                                display: 'block',
                                objectFit: 'cover',
                                height: '100%',
                                width: '100%',
                              }}
                              src={imageId ? `${imagesUri}/${imageId}?orderme-api-key=${apiKey}` : undefined}
                              alt={`${service.name} ${index + 1}`}
                              onLoad={() => index === 0 && setIsImageLoaded(true)}
                              onError={() => index === 0 && setIsImageLoaded(true)}
                            />
                          </ImageListItem>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </Box>
                  {serviceImages.length > 1 && (
                    <Box
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        mt: '10px',
                        '& .swiper-slide': {
                          opacity: 0.5,
                          cursor: 'pointer',
                        },
                        '& .swiper-slide-thumb-active': {
                          opacity: 1,
                        },
                        '& .swiper-button-next, & .swiper-button-prev': {
                          backgroundColor: 'rgba(255,255,255,0.7)',
                          borderRadius: '50%',
                          width: '25px',
                          height: '25px',
                          padding: '5px',
                          color: 'primary.main',
                          opacity: 0,
                          transition: 'opacity 0.2s ease',
                        },
                        '&:hover .swiper-button-next:not(.swiper-button-disabled), &:hover .swiper-button-prev:not(.swiper-button-disabled)':
                          {
                            opacity: 1,
                          },
                      }}
                    >
                      <Swiper
                        modules={[Thumbs, Navigation]}
                        navigation
                        slidesPerView={visibleImagesCount}
                        spaceBetween={10}
                        watchSlidesProgress
                        onSwiper={setThumbsSwiper}
                        style={{ height: '100px', width: `calc(${visibleImagesCount * 100}px + 4px)`, padding: '2px' }}
                      >
                        {serviceImages.map((imageId, index) => (
                          <SwiperSlide key={imageId ?? index}>
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '5px',
                                overflow: 'hidden',
                                boxSizing: 'border-box',
                                border: '1px solid transparent',
                                '.swiper-slide-thumb-active &': {
                                  border: `1px solid ${theme.palette.secondary.main}`,
                                },
                              }}
                            >
                              <ImageLoader
                                src={imageId ? `${imagesUri}/${imageId}?orderme-api-key=${apiKey}` : undefined}
                                alt={`${service.name} ${index + 1}`}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </Box>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </Box>
                  )}
                  <ActionBox actionType={actionType} service={service} visible={!isDownMediumScreen} />
                </Grid>
              )}
              <Grid size="grow" sx={{ textAlign: 'center', pl: { xs: '5px', md: '0px' }, pr: '5px' }}>
                <Typography sx={{ fontSize: '28px', px: '15px' }}>{service.name}</Typography>
                <Box>
                  <HtmlViewer content={description} />
                </Box>
                {isDescriptionExpandable && (
                  <Box sx={{ textAlign: 'left', px: '15px', pb: '10px', mt: '-15px' }}>
                    <Link
                      component="button"
                      underline="hover"
                      color="textLink"
                      fontWeight="bold"
                      sx={{ display: 'flex', alignItems: 'center', fontSize: '16px' }}
                      onClick={() => (isExpanded ? collapseDescription() : expandDescription())}
                    >
                      {isExpanded ? (
                        <>
                          {t('services:lessDetails')}
                          <ExpandLessIcon sx={{ color: 'textLink' }} />
                        </>
                      ) : (
                        <>
                          {t('services:moreDetails')}
                          <ExpandMoreIcon sx={{ color: 'textLink' }} />
                        </>
                      )}
                    </Link>
                  </Box>
                )}
                <ActionBox actionType={actionType} service={service} visible={isDownMediumScreen} isSticky />
              </Grid>
            </Grid>
          </Box>
          {appStyleMode === AppStyle.Mobile && <BottomCartBar />}
        </Grid>
      )}
    </>
  );
}
